import * as React from 'react';
import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import { ErrorBoundary } from 'Common/common-ui';
import { logFrontendError } from 'Common/services/api';
import CollapseExpand from './CollapseExpand';
import NavLinkList from './NavLinkList';
import { Collapse } from '@mui/material';
import { BrandColors } from 'Common/common-ui/common/theme';
import { isSmallScreen } from 'Common/util';
import { NavOption } from 'src/scripts/content';

export const drawerWidth = 250;

enum DrawerType {
  EXPAND,
  COLLAPSE,
}

// This left nav is inspired by the Persistent drawer and Clipped drawer here:
// https://mui.com/components/drawers/
// https://github.com/mui-org/material-ui/blob/v5.2.4/docs/src/pages/components/drawers/PersistentDrawerLeft.tsx
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flex: 1,
  display: 'flex',
  width: '100%',
  overflow: 'auto',
  overflowX: 'hidden',
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  padding: 0,
  margin: 0,
  backgroundColor: BrandColors.White,
}));

export default function LeftNav(props: {
  navOptions: NavOption;
  children: ReactNode;
  setOpen: (open: boolean) => void;
  open: boolean;
}): ReactNode {
  const { navOptions, setOpen, open } = props;
  const smallScreen = isSmallScreen();

  function collapseBar(): ReactNode {
    if (smallScreen) {
      return null;
    }

    return (
      <CollapseExpand
        handleToggle={(toggle) => setOpen(toggle)}
        type={open ? DrawerType.COLLAPSE : DrawerType.EXPAND}
        style={{
          left: open ? drawerWidth : 0,
          display: 'flex',
          alignItems: 'center',
        }}
      />
    );
  }

  return (
    // TODO: Update to use theme instead of CSS.
    <Box
      className="left-nav"
      display={'flex'}
      overflow={'hidden'}
      data-testid="left-nav-content"
      sx={{
        height: '100%',
      }}
    >
      {navOptions && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          <Drawer
            sx={{
              '& .MuiDrawer-paper': {
                position: smallScreen ? 'absolute' : 'relative',
              },
              flexShrink: 1,
              display: 'flex',
              zIndex: smallScreen ? '1000' : 'auto',
            }}
            id="left-nav-drawer"
            open={open}
            onClose={() => setOpen(false)}
            variant={smallScreen ? 'temporary' : 'permanent'}
            anchor="left"
            ModalProps={{
              keepMounted: false,
            }}
          >
            <Collapse orientation="horizontal" in={open}>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                }}
              >
                <NavLinkList
                  handleLeftNavCollapse={() => {
                    setOpen(!smallScreen);
                  }}
                  routeLinks={navOptions}
                />
              </Box>
            </Collapse>
          </Drawer>
          {collapseBar()}
          <Main
            id={'main-comp'}
            open={open}
            sx={{
              flexDirection: 'column',
            }}
          >
            <ErrorBoundary onError={logFrontendError}>
              {props.children}
            </ErrorBoundary>
          </Main>
        </Box>
      )}
    </Box>
  );
}
